<template>
  <v-container fluid class="page-wrap">
    <!-- ===== 操作エリア ===== -->
    <v-card outlined class="control-card mb-4">
      <v-card-text class="pa-4">
        <v-row align="start">
          <!-- 対象選択 -->
          <v-col cols="12" md="4" lg="3">
            <v-select v-model="value" :items="items" item-text="label" item-value="value" outlined dense hide-details
              label="対象を選択" class="control-field" />
          </v-col>

          <!-- 単日 / 期間 -->
          <v-col cols="6" sm="3" md="2" lg="1">
            <v-btn block class="fixed-height-btn" :color="dateMode === 'single' ? 'primary' : ''" outlined
              @click="dateMode = 'single'">
              単日指定
            </v-btn>
          </v-col>

          <v-col cols="6" sm="3" md="2" lg="1">
            <v-btn block class="fixed-height-btn" :color="dateMode === 'range' ? 'primary' : ''" outlined
              @click="dateMode = 'range'">
              期間指定
            </v-btn>
          </v-col>

          <!-- 単日 -->
          <v-col v-if="dateMode === 'single'" cols="12" sm="6" md="3" lg="3">
            <v-menu v-model="singleMenu" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatJa(singleDate)" readonly outlined dense hide-details v-bind="attrs"
                  v-on="on" prepend-inner-icon="mdi-calendar" class="control-field" />
              </template>
              <v-date-picker v-model="singleDate" :min="computedMinDate" :max="today" locale="ja-jp"
                @input="singleMenu = false" />
            </v-menu>
          </v-col>

          <template v-if="dateMode === 'range'">
            <v-col cols="12" sm="6" md="3" lg="2" class="date-col">
              <div class="date-field-wrap">
                <div class="date-label">FROM</div>
                <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="fromText" readonly outlined dense hide-details v-bind="attrs" v-on="on"
                      class="control-field" />
                  </template>
                  <v-date-picker v-model="from" :min="computedMinDate" :max="to || today" locale="ja-jp"
                    @input="fromMenu = false" />
                </v-menu>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3" lg="2" class="date-col">
              <div class="date-field-wrap">
              <div class="date-label">TO</div>
              <v-menu v-model="toMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="toText" readonly outlined dense hide-details v-bind="attrs" v-on="on"
                    class="control-field" />
                </template>
                <v-date-picker v-model="to" :min="from" :max="today" locale="ja-jp" @input="toMenu = false" />
              </v-menu>
              </div>
            </v-col>
          </template>

          <!-- 表示日付 -->
          <v-col cols="12" md="12" lg="3" class="d-flex align-center justify-lg-end">
            <div class="display-date mr-3">
              {{ displayText }}
            </div>

            <v-btn class="fixed-height-btn" outlined @click="onReset">
              期間指定初期化
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- ===== 曜日フィルター + 実行ボタン ===== -->
    <v-card outlined class="control-card mb-4">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" lg="5">
            <div class="section-label mb-2">曜日フィルター</div>

            <v-card outlined class="dow-box" :disabled="dateMode === 'single'">
              <div class="d-flex align-center flex-wrap px-3 py-2">
                <v-checkbox v-for="d in dowOptions" :key="d.value" v-model="selectedDows" :label="d.label"
                  :value="d.value" hide-details dense class="dow-check mr-3 mt-0 pt-0" />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="7">
            <div class="section-label mb-2">表示切替</div>

            <div class="action-buttons">
              <v-btn class="fixed-height-btn action-btn" outlined @click="onDaily">
                日別タッチ数
              </v-btn>

              <v-btn class="fixed-height-btn action-btn" outlined @click="onHourly">
                時間帯別タッチ数
              </v-btn>

              <v-btn class="fixed-height-btn action-btn" outlined @click="onDow">
                曜日別タッチ数
              </v-btn>

              <v-btn class="fixed-height-btn action-btn" outlined @click="openChartPicker">
                コンテンツ案内比率
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- ===== グラフ表示 ===== -->
    <v-card elevation="2" rounded="lg" class="chart-card">
      <v-card-title class="py-4 px-5">
        <div class="d-flex align-center">
          <v-icon class="mr-2">
            {{ mode === 'radar' ? 'mdi-chart-areaspline' : 'mdi-chart-bar' }}
          </v-icon>

          <div>
            <div class="text-h6 font-weight-bold">{{ chartTitle }}</div>
            <div class="chart-subtitle">
              選択条件に応じた集計結果を表示します
            </div>
          </div>
        </div>

        <v-spacer />

        <v-progress-circular v-if="loading" indeterminate size="20" />
      </v-card-title>

      <v-divider />

      <v-card-text class="px-5 pt-5 pb-6">
        <v-skeleton-loader v-if="loading" type="image" :height="chartHeight" />

        <v-alert v-else-if="errorMsg" type="error" dense outlined class="mb-4">
          {{ errorMsg }}
        </v-alert>

        <div v-else-if="!hasData" class="empty-state">
          上のボタンを押すと、グラフが表示されます。
        </div>

        <apexchart v-else :type="chartType" :height="chartHeight" :options="chartOptions" :series="series" />
      </v-card-text>
    </v-card>

    <!-- ===== 円グラフの表示対象を選択欄 ===== -->
    <v-dialog v-model="chartPicker.show" max-width="520">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          表示する対象を選択
        </v-card-title>

        <v-card-text>
          <v-select v-model="chartPicker.targets" :items="chartSelectableItems" item-text="label" item-value="value"
            multiple chips outlined dense hide-details label="対象（複数選択可）" />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeChartPicker">キャンセル</v-btn>
          <v-btn color="primary" @click="runChartByPicker">表示</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { DATA_LABEL_MAP } from '@/config/dataLabelMap'

export default {
  

  data() {

    //---------------------------------
    //
    // 初期値作成ローカル変数＆ローカル関数
    //
    //---------------------------------
    //今日の日付取得
    const today = new Date()
    //1→01にする処理
    const pad = (n) => String(n).padStart(2, '0')
    //文字列に変換（JSのルールで月は0月スタートなので＋１する必要がある）
    const toISO = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

    return {

      //初期設定（単日設定）
      dateMode: 'single',
      //初期設定（今日の日付け）
      singleDate: toISO(today),
      //単日カレンダーの開閉制御用（初期表示でカレンダーを開かせないため）
      singleMenu: false,

      //初期表示
      value: 'all',

      items: [
        { label: 'ALL TOUCH', value: 'all' },
        { label: '施設マップ', value: 'facility_name' },
        { label: 'カテゴリ選択', value: 'facility_category' },
        { label: '言語', value: 'langage' },
        { label: 'イラスト', value: 'illustration' },
        { label: 'TOP UI', value: 'top' },
        { label: 'ナビビジョン', value: 'sponsor_vision' },
        { label: 'スポンサーマップ', value: 'sponsor_map' },
      ],


      fromMenu: false,
      toMenu: false,

      //FROM初期値
      minDate: '2026-01-01',
      today: toISO(today),

      //TO初期値
      from: toISO(today),
      to: toISO(today),

      // 曜日チェック（表示用）
      dowOptions: [
        { label: '日', value: 0 },
        { label: '月', value: 1 },
        { label: '火', value: 2 },
        { label: '水', value: 3 },
        { label: '木', value: 4 },
        { label: '金', value: 5 },
        { label: '土', value: 6 }
      ],

      // 初期設定：全部ON
      selectedDows: [0, 1, 2, 3, 4, 5, 6],

      // グラフ表示用のデータ
      mode: null,
      loading: false,
      errorMsg: '',

      //====ApexCharts用====

      //データ表示本体
      series: [{ name: '件数', data: [] }],
      //グラフの「見た目・動き・設定」を全部管理
      chartOptions: {
        //チャートの上部ツールバーだけ非表示
        chart: { toolbar: { show: false } },
        //X軸
        xaxis: { categories: [] }
      },


      // ===== グラフ対象選択ポップアップインスタンス作成（ALL TOUCHなし） =====
      chartPicker: {
        show: false, //ボタンを押したときに表示する(初期値はfalse）・（ボタンを押すとtrue)
        //選択初期値（openChartPickerで代入）
        targets: [],
      }

    }
  },

  //computed: →元のデータが変わったら、自動で変更
  computed: {
    //TOの日付から3ヶ月前しか遡れない
    computedMinDate() {
      //toがなければ、設定の2026/1/1に設定される
      if (!this.to) return this.minDate
      //今日の日付をDate型に変換
      const d = new Date(this.to)
      //3か月前のデータまで取得
      d.setMonth(d.getMonth() - 3)
      //1月を01などにする →padStartは1桁のときは、0を追加する 2を設定しているので2桁にする
      const pad = (n) => String(n).padStart(2, '0')
      //ｄのgetFullYear（西暦を取得）上で作成したpadを使用し（月）を取得 0月になるので、+1月にする 日も同様
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },

    fromText() {
      return this.formatJa(this.from)
    },

    toText() {
      return this.formatJa(this.to)
    },

    //期間指定の場合の処理【表示日時】
    rangeText() {
      if (!this.from || !this.to) return ''
      // 同日なら 1つ表記にする
      if (this.from === this.to) return this.formatJa(this.from)
      return `${this.formatJa(this.from)}～${this.formatJa(this.to)}`
    },

    // 表示用を1本化（単日/期間をここで吸収）
    displayText() {
      if (this.dateMode === 'single') return this.formatJa(this.singleDate)
      return this.rangeText
    },

    chartTitle() {
      // モードに応じてタイトルを返す処理を整理
      if (this.mode === 'daily') return '日別タッチ数'
      if (this.mode === 'hourly') return '時間帯別タッチ数'
      if (this.mode === 'dow') return '曜日別タッチ数'
      if (this.mode === 'radar') return 'コンテンツ案内比率'
      return 'グラフ'
    },

    // チャート用グラフ
    chartSelectableItems() {
      //itemsから'all'以外を残す
      return this.items.filter(function (x) {
        return x.value !== 'all';
      });
    },


    // ----------------------------------------------------------
    //
    //----------------------------------------------------------

    // ★ series にデータがあるか（空なら初期メッセージ表示にする）
    hasData: function () {
      if (!Array.isArray(this.series) || this.series.length === 0) return false;
      if (!this.series[0] || !Array.isArray(this.series[0].data)) return false;
      return this.series[0].data.length > 0;
    },

    // ★ bar / radar をここで統一判定
    chartType: function () {
      return this.mode === 'radar' ? 'radar' : 'bar';
    },

    // ★ 画面サイズに応じて高さを自動調整（700固定を廃止）
    chartHeight: function () {
      var bp = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint : null;
      var isXs = bp ? bp.xsOnly : false;

      if (isXs) return 320;
      if (this.mode === 'daily') return 520;
      if (this.mode === 'radar') return 460;
      return 420;
    }


  },

  // モード切替時に日付を同期して「変化しない時がある」を減らす
  watch: {
    dateMode: function (newVal) {
      if (newVal === 'single') {
        // 単日へ切替：期間の from を単日にコピー（好みで to でもOK）
        this.singleDate = this.from || this.today
      } else if (newVal === 'range') {
        // 期間へ切替：単日を from/to にコピー（同日にしておくと自然）
        this.from = this.singleDate || this.today
        this.to = this.singleDate || this.today
      }
    }
  },

  //ボタンなどのイベント発生時に実行
  methods: {
    //日付をISOの日付文字列にする（引数）
    formatJa(iso) {
      //nullの場合は空白
      if (!iso) return ''
      //-をsplitにし配列に入れる
      const parts = iso.split('-')
      const y = parts[0]
      const m = parts[1]
      const d = parts[2]
      return `${y}年${Number(m)}月${Number(d)}日`
    },

    //バインドするデータ
    buildPayload() {
      // 単日モードの場合は FROM/TO に同じ日付を入れる
      const isSingle = this.dateMode === 'single'

      return {
        items: this.value, //選択された項目を送信
        from: isSingle ? this.singleDate : this.from,
        to: isSingle ? this.singleDate : this.to,
        // 単日の場合は曜日指定を全てONにする（0-6）、期間の場合は選択された曜日を送る
        dows: isSingle ? [0, 1, 2, 3, 4, 5, 6] : this.selectedDows
      }


    },

    //初期値に戻す
    onReset() {
      this.from = this.today
      this.to = this.today
      // 修正：単日用データもリセット
      this.singleDate = this.today
    },



    //レーダーチャートの初期値
    openChartPicker() {

      //すでにtargetsが入ってるなら上書きしない
      if (!Array.isArray(this.chartPicker.targets) || this.chartPicker.targets.length === 0) {
        // 初回は全選択にする為（itemeからall抜きを選択し、valueを選択指定し、代入する）
        this.chartPicker.targets = this.chartSelectableItems.map(function (x) { return x.value })
      }

      //ボタンを押したときに、chartPicker.showがtrueになり、v-dialogが表示される
      this.chartPicker.show = true
    },

    //キャンセルボタンを押すとポップアップ閉じる
    closeChartPicker() {
      this.chartPicker.show = false
    },

    //レーダーチャートのポップアップイベント
    runChartByPicker() {

      // 未選択防止→未選択の場合、注意書き表示
      if (this.chartPicker.targets.length === 0) {
        //Vueのデータを書き換えている
        this.errorMsg = '対象を選択してください'
        return
      }

      //表示ボタン押すとダイヤログをOFF
      this.chartPicker.show = false

      //レーダーチャートの時に実行する
      this.onRatio(this.chartPicker.targets)
      return

    },

    //期間指定が3日以上か確認するイベント
    isStackedMode() {
      //期間指定じゃない場合はreturn
      if (this.dateMode !== 'range') return false

      var start = new Date(this.from)
      var end = new Date(this.to)
      //getTimeにする理由は、日付の引き算はできないからgetTimeにしてから計算する
      var diff = end.getTime() - start.getTime()
      //getTimeから日付に変更する→floorは小数点切り捨て開始日も追加する為、＋１にする
      var days = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1

      return days >= 3
    },

    //--------------------------------------
    //
    // 日別タッチ
    // 3日以上のデータがきた場合は、棒グラフ、積み上げに変わる
    //
    //--------------------------------------

    onDaily() {
      var payload = this.buildPayload()
      this.mode = 'daily'
      this.loading = true
      this.errorMsg = ''

      //axiosでbackに送信
      this.$axios.post('/api/touch/daily', payload)
        //レスポンス
        .then((res) => {

          // データの形式に合わせて、グラフ用のデータを整形
          var rows = res.data.rows || res.data.message || []
          // rowsの中身を確認するためのログ
          //console.log('res:', res)//メッセージ全体
          //console.log('rows:', rows)//rowsの中身

          // データがない場合は、seriesを空にしてエラーメッセージを表示
          if (!rows.length) {
            this.series = []
            this.errorMsg = 'データがありません'
            return
          }

          // 日付だけの配列を作成（x軸のカテゴリ用）
          //map →「配列の中身を1つずつ取り出して、加工して、新しい配列を作る」
          var categories = rows.map(r => r.target_date)

          //★ALL TOUCH の場合
          if (this.value === 'all') {

            this.series = [
              {
                name: '全体合計',
                data: rows.map(r => Number(r.total || 0))
              }
            ]

            this.chartOptions = {
              chart: {
                type: 'bar',//棒グラフ
                stacked: false,
                toolbar: { show: false }
              },
              xaxis: { categories: categories }
            }

            return

          }

          //★通常時
          // "data"で始まるキーを自動で取得（例: ["data1", "data2", ... ]）
          var dataKeys = Object.keys(rows[0]).filter(key => key.startsWith('data'))

          var mapByTarget = DATA_LABEL_MAP[this.value] || {}

          var series = dataKeys.map(function (keyName) {
            return {
              name: mapByTarget[keyName] || keyName,
              data: rows.map(function (r) {
                return Number(r[keyName] || 0)
              })
            }
          })
          // dataの中身をログで確認（最初の系列だけ例示）
          if (series.length > 0) console.log('系列1のデータ:', series[0].data)


          //3日以上か確認フラグ
          var isStacked = this.isStackedMode()

          this.chartOptions = {
            chart: {
              type: 'bar',// 棒グラフを指定
              stacked: isStacked, // 動的にする
              toolbar: { show: false } // グラフのツールバーを表示
            },

            //DataLabels を無効化（警告＆重さ対策）
            dataLabels: {
              enabled: false
            },

            plotOptions: {
              bar: {
                //棒グラフの幅を調整（初期: 50% → 積み上げ時は少し太くする）
                columnWidth: isStacked ? '70%' : '50%'
              }
            },
            xaxis: {
              // x軸のカテゴリを、作成したcategoriesの配列にする
              categories: categories
            },
            yaxis: {
              // y軸のタイトル現在は空（必要に応じて変更）
              title: { text: '' }
            },
            tooltip: {
              y: {
                // ツールチップの値に「件」をつける
                formatter: val => val + ' 件'
              }
            },
            legend: {
              // 凡例をグラフの上に表示
              position: 'top'
            }
          }

          // 最後に、グラフのデータを更新
          this.series = series
        })
        .catch((err) => {
          console.log(err)
          this.errorMsg = 'サーバーエラー'
        })
        .finally(() => {
          this.loading = false
        })
    },

    //---------------------
    //
    // 時間帯別タッチ
    //
    //----------------------
    onHourly() {
      var payload = this.buildPayload()
      this.mode = 'hourly'
      this.loading = true
      this.errorMsg = ''

      this.$axios.post('/api/touch/time', payload)

        .then((res) => {
          // データの形式に合わせて、グラフ用のデータを整形
          var rows = res.data.rows || res.data.message || []
          // rowsの中身を確認するためのログ
          //console.log('res:', res)//メッセージ全体
          //console.log('rows:', rows)//rowsの中身

          // データがない場合は、seriesを空にしてエラーメッセージを表示
          if (!rows.length) {
            this.series = []
            this.errorMsg = 'データがありません'
            return
          }

          // 時間配列を作成（x軸のカテゴリ用）
          //map →「配列の中身を1つずつ取り出して、加工して、新しい配列を作る」
          var categories = rows.map(r => r.hour_label)


          //ALL TOUCH の場合
          if (this.value === 'all') {

            this.series = [
              {
                name: '全体合計',
                data: rows.map(r => Number(r.total || 0))
              }
            ]

            this.chartOptions = {
              chart: {
                type: 'bar',//棒グラフ
                stacked: false,
                toolbar: { show: false }
              },
              xaxis: { categories: categories }
            }

            return

          }


          // "data"で始まるキーを自動で取得（例: ["data1", "data2", ... ]）
          var dataKeys = Object.keys(rows[0]).filter(key => key.startsWith('data'))

          var mapByTarget = DATA_LABEL_MAP[this.value] || {}

          var series = dataKeys.map(function (keyName) {
            return {
              name: mapByTarget[keyName] || keyName,
              data: rows.map(function (r) {
                return Number(r[keyName] || 0)
              })
            }
          })

          // dataの中身をログで確認（最初の系列だけ例示）
          //if (series.length > 0) console.log('系列1のデータ:', series[0].data)


          //3日以上か確認フラグ
          var isStacked = this.isStackedMode()

          this.chartOptions = {
            chart: {
              type: 'bar',// 棒グラフを指定
              stacked: isStacked, // 積み上げなし横並びに指定
              toolbar: { show: false } // グラフのツールバーを表示
            },

            //DataLabels を無効化（警告＆重さ対策）
            dataLabels: {
              enabled: false
            },

            plotOptions: {
              bar: {
                //棒グラフの幅を調整（初期: 50%）
                columnWidth: '70%'
              }
            },
            xaxis: {
              // x軸のカテゴリを、作成したcategoriesの配列にする
              categories: categories
            },
            yaxis: {
              // y軸のタイトル現在は空（必要に応じて変更）
              title: { text: '' }
            },
            tooltip: {
              y: {
                // ツールチップの値に「件」をつける
                formatter: val => val + ' 件'
              }
            },
            legend: {
              // 凡例をグラフの上に表示
              position: 'top'
            }
          }

          // 最後に、グラフのデータを更新
          this.series = series
        })
        .catch(() => {
          //console.log(err)
          this.errorMsg = 'サーバーエラー'
        })
        .finally(() => {
          this.loading = false
        })
    },

    //-------------------------
    //
    // 曜日別タッチ
    //
    //-------------------------
    onDow() {

      var payload = this.buildPayload()
      this.mode = 'dow'
      this.loading = true
      this.errorMsg = ''

      this.$axios.post('/api/touch/weekday', payload)
        .then((res) => {
          var rows = res.data.rows || res.data.message || []
          //console.log('res:', res)
          //console.log('rows:', rows)

          if (!rows.length) {
            this.series = []
            this.errorMsg = 'データがありません'
            return
          }

          // ===== 必ず「日月火水木金土」順にしたい（保険） =====
          // rowsには { dow:0..6, dow_label:'日'..'土', data1.. } が来る想定
          var order = ['日', '月', '火', '水', '木', '金', '土']
          rows.sort((a, b) => order.indexOf(a.dow_label) - order.indexOf(b.dow_label))

          // ===== X軸カテゴリ：曜日ラベル =====
          var categories = rows.map(r => r.dow_label)


          //★ALL TOUCH の場合
          if (this.value === 'all') {

            this.series = [
              {
                name: '全体合計',
                data: rows.map(r => Number(r.total || 0))
              }
            ]

            this.chartOptions = {
              chart: {
                type: 'bar',//棒グラフ
                stacked: false,
                toolbar: { show: false }
              },
              xaxis: { categories: categories }
            }

            return

          }

          // "data"で始まるキーを自動で取得（例: ["data1", "data2", ... ]）
          var dataKeys = Object.keys(rows[0]).filter(key => key.startsWith('data'))

          var mapByTarget = DATA_LABEL_MAP[this.value] || {}

          var series = dataKeys.map(function (keyName) {
            return {
              name: mapByTarget[keyName] || keyName,
              data: rows.map(function (r) {
                return Number(r[keyName] || 0)
              })
            }
          })

          //if (series.length > 0) console.log('系列1のデータ:', series[0].data)

          //3日以上か確認フラグ
          var isStacked = this.isStackedMode()

          this.chartOptions = {
            chart: {
              type: 'bar',
              stacked: isStacked,
              toolbar: { show: false }
            },

            //DataLabels を無効化（警告＆重さ対策）
            dataLabels: {
              enabled: false
            },

            plotOptions: {
              bar: {
                columnWidth: '70%'
              }
            },
            xaxis: {
              categories: categories
            },
            yaxis: {
              title: { text: '' }
            },
            tooltip: {
              y: {
                formatter: function (val) { return val + ' 件' } // ← lambda使わず
              }
            },
            legend: {
              position: 'top'
            }
          }

          this.series = series
        })
        .catch(() => {
          //console.log(err)
          this.errorMsg = 'サーバーエラー'
        })
        .finally(() => {
          this.loading = false
        })
    },


    //--------------------------------------
    //
    // コンテンツ案内比率（レーダーチャート）
    // ※ 各テーブルの「data列合計の合計」を比較する
    // 引数は配列（ALL以外の配列）
    //
    //---------------------------------------
    async onRatio(targets) {

      try {

        // 表示モードをレーダーに変更
        this.mode = 'radar';
        this.loading = true;
        this.errorMsg = '';

        // ===== targets の正規化（配列にする＆空文字除外）=====
        if (!Array.isArray(targets)) targets = [];

        //引数【配列】を確認し、!!xの条件で、falseのものは、除外する
        //配列の要素数分だけ関数が実行
        targets = targets.filter(function (x) { return !!x; });

        if (targets.length === 0) {
          //.series→グラフデータ
          //グラフを空にする表示しない
          this.series = [];
          //chartOptionsの一部修正→新しいオブジェクトを作る
          //空の｛｝を作ることで、新しいオブジェクトになる
          this.chartOptions = Object.assign({}, this.chartOptions, {
            //X軸も空にする
            xaxis: { categories: [] }
          });
          this.errorMsg = '表示対象が選択されていません';
          return;
        }

        // ===== payload を buildPayload() ベースで作成 =====
        // buildPayloadが無い場合は、from/to/value/selectedDows を直接使うように差し替えてOK
        var payload;

        //buildPayloadの関数があるか確認
        if (typeof this.buildPayload === 'function') {
          //存在する場合は、専用ロジック生成（API送信用）
          payload = this.buildPayload();
        } else {
          //エラー対策（保険）
          payload = {
            from: this.from,
            to: this.to,
            dows: Array.isArray(this.selectedDows) ? this.selectedDows : []
          };
        }

        // items は targets に差し替え（レーダーの軸＝選択したテーブル）
        payload.items = targets;

        // ===== 「曜日が空配列で来たら表示なし」仕様ならここで止める（任意）=====
        // バック側でも rows:[] を返す仕様なら、ここで止めてもOK
        if (Array.isArray(payload.dows) && payload.dows.length === 0) {
          this.series = [];
          this.chartOptions = Object.assign({}, this.chartOptions, {
            xaxis: { categories: [] }
          });
          this.errorMsg = '曜日が未選択のため表示しません';
          return;
        }

        // デバッグ
        //console.log('[RADAR] payload:', JSON.stringify(payload));

        // ===== API呼び出し =====
        var res = await this.$axios.post('/api/touch/chartTouch', payload);
        var api = res && res.data ? res.data : null;

        //console.log('[RADAR] response:', api);

        var rows = (api && api.rows) ? api.rows : [];

        // rows が空なら表示しない
        if (!rows || rows.length === 0) {
          this.series = [];
          this.chartOptions = Object.assign({}, this.chartOptions, {
            xaxis: { categories: [] }
          });
          this.errorMsg = 'データがありません';
          return;
        }

        // ===== tableName -> total の辞書を作る =====
        var totalByTable = {};
        for (var i = 0; i < rows.length; i++) {
          var r = rows[i];
          if (r && r.tableName) {
            totalByTable[r.tableName] = Number(r.total || 0);
          }
        }

        // ===== X軸ラベル（選択順を維持）=====
        // items配列（label/value）を持っているなら label に変換、なければ tableName をそのまま表示
        var categories = [];
        for (var j = 0; j < targets.length; j++) {
          var tableName = targets[j];

          var label = tableName;
          if (Array.isArray(this.items)) {
            var hit = this.items.find(function (it) { return it.value === tableName; });
            if (hit && hit.label) label = hit.label;
          }

          categories.push(label);
        }

        // ===== series data（選択順を維持）=====
        var data = [];
        for (var k = 0; k < targets.length; k++) {
          var t = targets[k];
          data.push(Number(totalByTable[t] || 0));
        }

        // デバッグ
        //console.log('[RADAR] targets:', targets);
        //console.log('[RADAR] categories:', categories);
        //console.log('[RADAR] data:', data);

        // ===== ApexCharts に反映 =====
        this.chartOptions = Object.assign({}, this.chartOptions, {
          chart: {
            type: 'radar',
            toolbar: { show: false }
          },

          //DataLabels を無効化（警告＆重さ対策）
          dataLabels: {
            enabled: false
          },

          xaxis: {
            categories: categories
          },
          tooltip: {
            y: {
              formatter: function (val) { return String(val) + ' 件'; }
            }
          },
          legend: {
            position: 'top'
          }
        });

        this.series = [{
          name: '合計',
          data: data
        }];

      } catch (e) {
        //console.error(e);
        this.series = [];
        this.errorMsg = 'サーバーエラー';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>


<style scoped>
.page-wrap {
  max-width: 1360px;
  margin: 0 auto;
  background: #f7f8fa;
  min-height: 100vh;
  padding-top: 20px;
  padding-bottom: 24px;
}

.control-card,
.chart-card {
  border-radius: 12px;
  background: #ffffff;
}

.control-field {
  background: #ffffff;
}

.display-date {
  white-space: nowrap;
  font-size: 11px;
  color: #555;
  white-space: nowrap;
  text-align: right;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #666;
}

.dow-box {
  border-radius: 10px;
  background: #fafafa;
}

.dow-check {
  margin-bottom: 0 !important;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.action-btn {
  min-width: 160px;
}

.fixed-height-btn {
  height: 40px !important;
}

.chart-subtitle {
  font-size: 12px;
  color: #777;
  margin-top: 2px;
}

.empty-state {
  color: #666;
  text-align: center;
  padding: 64px 0;
  font-size: 14px;
}

.date-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  padding-left: 4px;
  line-height: 1;
}

.control-field {
  background: #fff;
}

.date-col {
  display: flex;
  align-items: flex-start;
}

.date-field-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.date-field-wrap .v-menu {
  width: 100%;
}

.date-label {
  font-size: 12px;
  color: #666;
  margin: 0 0 4px 4px;
  line-height: 1;
  min-height: 12px;
}



@media (max-width: 1264px) {
  .action-btn {
    min-width: 140px;
  }
}

@media (max-width: 960px) {
  .display-date {
    margin-top: 8px;
  }

  .action-buttons {
    margin-top: 4px;
  }
}
</style>
